/**
 * ArtifactList — downloadable files the agent produced for one answer
 * (a workbook, a memo, a chart PNG), rendered inside the chat.
 *
 * The backend returns them as metadata only (`response_meta.artifacts`); the
 * bytes are fetched on demand from GET /api/chat/artifacts/{artifact_id},
 * authenticated like every other API call, and handed to the browser as a blob
 * download under the artifact's own filename.
 */
import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';

/** "812 B" / "44 KB" / "1.4 MB" — '' when the size is unknown. */
export function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ICON_BY_KIND = { xlsx: FileSpreadsheet, docx: FileText, png: ImageIcon };

export default function ArtifactList({ artifacts, darkMode }) {
  const { fetchWithAuth } = useAuth();
  // artifact_id -> 'loading' | 'error' (absent = idle/done)
  const [status, setStatus] = useState({});

  const rows = Array.isArray(artifacts) ? artifacts : [];
  if (rows.length === 0) return null;

  const setRowStatus = (id, value) =>
    setStatus((prev) => {
      const next = { ...prev };
      if (value) next[id] = value;
      else delete next[id];
      return next;
    });

  const download = async (artifact) => {
    const id = artifact.artifact_id;
    if (status[id] === 'loading') return;
    setRowStatus(id, 'loading');
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.chatArtifact(id));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = artifact.filename || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setRowStatus(id, null);
    } catch (e) {
      console.error('Artifact download failed', e);
      setRowStatus(id, 'error');
    }
  };

  const muted = darkMode ? 'text-white/50' : 'text-gray-500';

  return (
    <div className="mt-3 w-full max-w-2xl">
      <div
        className={`rounded-xl border overflow-hidden divide-y ${
          darkMode
            ? 'border-white/15 divide-white/10 bg-white/5'
            : 'border-gray-200 divide-gray-100 bg-black/[0.02]'
        }`}
      >
        {rows.map((artifact, i) => {
          const Icon = ICON_BY_KIND[artifact.kind] || FileText;
          const state = status[artifact.artifact_id];
          const size = formatBytes(artifact.size_bytes);
          return (
            <div key={artifact.artifact_id ?? i} className="flex items-center gap-3 px-4 py-3">
              <Icon className={`w-4 h-4 shrink-0 ${muted}`} />
              <div className="min-w-0 flex-1">
                <div className={`truncate text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {artifact.filename}
                </div>
                <div className={`text-xs ${muted}`}>
                  {[artifact.label, size].filter(Boolean).join(' · ')}
                </div>
                {state === 'error' && (
                  <div className="text-xs text-red-400">Download failed — try again</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => download(artifact)}
                disabled={state === 'loading'}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors disabled:opacity-40 ${
                  darkMode
                    ? 'border-white/20 text-white hover:bg-white/10'
                    : 'border-gray-300 text-gray-900 hover:bg-gray-100'
                }`}
              >
                {state === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                ) : (
                  <Download className="w-4 h-4 shrink-0" />
                )}
                {state === 'error' ? 'Retry' : 'Download'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
